import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { FilterCheckboxItem } from '@/components/organisms/SessionsInfiniteTable/filters/FilterCheckboxItem';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import useBrains from '@/hooks/useBrains';
import { applyFilters } from '@/modules/analytics/redux/actions';
import { AccountAnalytics, AnalyticsType } from '@/shared/types/analytics';
import { Brain } from '@/shared/types/brain';
import { OptionBase } from '@/shared/types/common';
import { RootState } from '@/shared/types/state';

type PopoverSide = 'top' | 'right' | 'bottom' | 'left';
type PopoverAlign = 'start' | 'center' | 'end';

interface AiAgentSelectPopoverProps {
  children: React.ReactNode;
  align?: PopoverAlign;
  side?: PopoverSide;
  sideOffset?: number;
}

const selectAccountFilters = (state: RootState): AccountAnalytics[] =>
  state.analytics.accountAnalytics ?? [];

/**
 * Multi-select popover for filtering the AI Agents analytics report by AI Agent.
 *
 * Reads the current `brainIds` selection from the `accountAnalytics` Redux
 * slice and dispatches `applyFilters` directly when the selection changes.
 * The existing `useAccountSessionFilters` hook active on the page picks up
 * the Redux change and re-syncs both the account filter form and the URL.
 */
export const AiAgentSelectPopover = ({
  children,
  align = 'start',
  side = 'top',
  sideOffset = 8,
}: AiAgentSelectPopoverProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const accountFilters = useSelector(selectAccountFilters);
  const { brains } = useBrains();
  const availableBrains = useMemo<Brain[]>(() => brains ?? [], [brains]);
  const totalBrains = availableBrains.length;

  const selectedBrainIds = useMemo<OptionBase[]>(() => {
    const filter = accountFilters.find((f) => f.type === 'brainIds');
    return Array.isArray(filter?.brainIds) ? filter.brainIds : [];
  }, [accountFilters]);

  const selectedBrainIdsSet = useMemo(
    () => new Set(selectedBrainIds.map((b) => b.value)),
    [selectedBrainIds]
  );

  const filteredBrains = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matches = query
      ? availableBrains.filter((brain) => brain.name.toLowerCase().includes(query))
      : availableBrains;
    // Float brains with insights enabled to the top so users can quickly find
    // agents eligible for the single-agent custom insights view. Stable sort
    // preserves the original (last_trained) order within each group.
    return [...matches].sort(
      (a, b) =>
        Number(b.metadata?.insights_enabled ?? false) -
        Number(a.metadata?.insights_enabled ?? false)
    );
  }, [availableBrains, search]);

  const allSelected = totalBrains > 0 && selectedBrainIds.length === totalBrains;

  const applyBrainSelection = useCallback(
    (nextBrainIds: OptionBase[]) => {
      const otherFilters = accountFilters.filter((f) => f.type !== 'brainIds');
      const nextFilters: AccountAnalytics[] =
        nextBrainIds.length > 0
          ? [...otherFilters, { type: 'brainIds', brainIds: nextBrainIds }]
          : otherFilters;
      dispatch(applyFilters({ type: AnalyticsType.ACCOUNT, filters: nextFilters }));
    },
    [accountFilters, dispatch]
  );

  const toggleBrain = useCallback(
    (brain: Brain, checked: boolean) => {
      const option: OptionBase = { value: brain.brain_id, label: brain.name };
      const nextBrainIds = checked
        ? [...selectedBrainIds.filter((b) => b.value !== option.value), option]
        : selectedBrainIds.filter((b) => b.value !== option.value);
      applyBrainSelection(nextBrainIds);
    },
    [applyBrainSelection, selectedBrainIds]
  );

  const toggleAllBrains = useCallback(
    (checked: boolean) => {
      if (checked) {
        applyBrainSelection(
          availableBrains.map((brain) => ({ value: brain.brain_id, label: brain.name }))
        );
      } else {
        applyBrainSelection([]);
      }
    },
    [applyBrainSelection, availableBrains]
  );

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setSearch('');
    }
  }, []);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align={align}
        side={side}
        sideOffset={sideOffset}
        className="w-72 overflow-hidden rounded-md border border-border bg-popover p-0 text-popover-foreground shadow-md"
      >
        <div className="border-b border-border px-3 py-2.5">
          <Input
            size="sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('ai_agents.search_ai_agent')}
            aria-label={t('ai_agents.search_ai_agent')}
            className="h-6 rounded-none border-none bg-transparent px-0 py-0 text-sm leading-5 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
            autoFocus
          />
        </div>

        <div className="flex flex-col gap-1 px-1 pt-1 pb-2">
          <div className="px-3 pt-2 pb-1 text-xs leading-4 font-medium text-muted-foreground">
            {t('brains.auto_ai.select_brain')}
          </div>

          <ScrollArea className="max-h-56">
            {filteredBrains.length === 0 ? (
              <div className="flex h-8 items-center px-2 text-sm leading-5 text-muted-foreground">
                {t('common.no_results')}
              </div>
            ) : (
              <>
                {!search.trim() && (
                  <FilterCheckboxItem
                    id="ai-agent-select-all"
                    checked={allSelected}
                    onCheckedChange={toggleAllBrains}
                  >
                    <span className="font-medium">
                      {t('brains.all_ai_agents')} ({totalBrains})
                    </span>
                  </FilterCheckboxItem>
                )}
                {filteredBrains.map((brain) => (
                  <FilterCheckboxItem
                    key={brain.brain_id}
                    id={`ai-agent-select-${brain.brain_id}`}
                    checked={selectedBrainIdsSet.has(brain.brain_id)}
                    onCheckedChange={(checked) => toggleBrain(brain, checked)}
                  >
                    {brain.name}
                  </FilterCheckboxItem>
                ))}
              </>
            )}
          </ScrollArea>
        </div>
      </PopoverContent>
    </Popover>
  );
};
